import type RouteInfo from '@ember/routing/route-info'
import { type ExportOption, isExportOption } from 'teacher-ui-client/components/reporting/export-select'

type RouteInfoWithMetadataRecord = {
  metadata: Record<string, unknown>
} & RouteInfo

type RouteInfoWithExportOptions = {
  metadata: {
    exportOptions: ExportOption[]
  }
} & RouteInfoWithMetadataRecord

type RouteInfoWithPageTitle = {
  metadata: {
    pageTitle: string
  }
} & RouteInfoWithMetadataRecord

export function hasMetadataRecord(maybeMetadata: RouteInfo): maybeMetadata is RouteInfoWithMetadataRecord {
  return (
    typeof maybeMetadata?.metadata === 'object' &&
    maybeMetadata.metadata !== null &&
    !Array.isArray(maybeMetadata.metadata)
  )
}

export function hasExportOptions(routeInfo: RouteInfo): routeInfo is RouteInfoWithExportOptions {
  return (
    hasMetadataRecord(routeInfo) &&
    'exportOptions' in routeInfo.metadata &&
    Array.isArray(routeInfo.metadata.exportOptions) &&
    routeInfo.metadata.exportOptions.every(isExportOption)
  )
}

export function hasPageTitle(routeInfo: RouteInfo): routeInfo is RouteInfoWithPageTitle {
  return (
    hasMetadataRecord(routeInfo) &&
    'pageTitle' in routeInfo.metadata &&
    typeof routeInfo.metadata.pageTitle === 'string'
  )
}

export function exportOptionsFromRouteInfo(routeInfo: Nullable<RouteInfo>): ExportOption[] | undefined {
  const rInfo = routeInfo?.find((r) => hasExportOptions(r))
  return rInfo && hasExportOptions(rInfo) ? rInfo.metadata.exportOptions : undefined
}

export function pageTitleFromRouteInfo(routeInfo: Nullable<RouteInfo>): string | undefined {
  const rInfo = routeInfo?.find((r) => hasPageTitle(r))
  return rInfo && hasPageTitle(rInfo) ? rInfo.metadata.pageTitle : undefined
}
